/**
 * Widget Liste - Affiche les offres touristiques sous forme de cards avec filtres
 */

import { html, unsafeCSS, CSSResultGroup } from "lit";
import { customElement, state } from "lit/decorators.js";
import { ref, createRef, Ref } from "lit/directives/ref.js";
import { BaseWidget } from "../components/BaseWidget";
import "../components/FilterPanel";
import "../components/WidgetHeader";
import "../components/WidgetLayout";
import "../components/Icon";
import { ObjetTouristique } from "../types/apidae.d";
import { DisplayedFieldPreviewId } from "../types/widgets.d";
import { StyleService } from "../services/StyleService";
import { getFieldData, getFieldIconName } from "../utils/itemFieldUtils";
import listWidgetStyles from "../styles/list-widget.css?inline";
import { formatDate, getImageUrl, getLabel, t } from "../utils/widgetsUtils";

@customElement("apidae-widget-liste")
export class ListWidget extends BaseWidget {
  static styles: CSSResultGroup = [
    BaseWidget.sharedStyles,
    unsafeCSS(listWidgetStyles),
  ];

  @state()
  private failedImages: Set<number> = new Set();

  @state()
  private loadedImages: Set<number> = new Set();

  private sentinelRef: Ref<HTMLDivElement> = createRef();
  private intersectionObserver?: IntersectionObserver;

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  firstUpdated() {
    // Configurer l'IntersectionObserver pour le scroll infini
    this.setupIntersectionObserver();
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);

    // Reconfigurer l'observer si le sentinel change
    if (changedProperties.has("items") || changedProperties.has("hasMore")) {
      requestAnimationFrame(() => {
        this.setupIntersectionObserver();
      });
    }

    if (
      this.changedNotNull(changedProperties, "currentPage") &&
      this.currentPage === 1
    ) {
      this.resetScroll();
    }
  }

  protected async handleFilterChange(e: CustomEvent) {
    super.handleFilterChange(e);

    // Réinitialiser le scroll en haut pour éviter que l'IntersectionObserver déclenche un chargement immédiat
    this.resetScroll();
  }

  protected async handleClearFilters() {
    super.handleClearFilters();

    // Réinitialiser le scroll en haut pour éviter que l'IntersectionObserver déclenche un chargement immédiat
    this.resetScroll();
  }

  private resetScroll() {
    requestAnimationFrame(() => {
      const scrollContainer = this.shadowRoot?.querySelector(
        ".list-main",
      ) as HTMLElement;
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
      // Réinitialiser l'IntersectionObserver pour qu'il ne déclenche pas immédiatement
      this.setupIntersectionObserver();
    });
  }

  private setupIntersectionObserver() {
    // Détruire l'ancien observer s'il existe
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    // Si pas de sentinel ou pas de données à charger, ne pas créer d'observer
    if (!this.sentinelRef.value || !this.hasMore) {
      return;
    }

    // Trouver le conteneur scrollable
    const scrollContainer = this.shadowRoot?.querySelector(
      ".list-main",
    ) as HTMLElement;
    if (!scrollContainer) {
      return;
    }

    // Créer un nouvel observer avec le conteneur scrollable comme root
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.hasMore && !this.loading) {
            if (this.isChildWidget) {
              this.dispatchEvent(
                new CustomEvent("child-context-change", {
                  detail: {
                    source: "list",
                    loadNextPage: true,
                  },
                  bubbles: true,
                  composed: true,
                }),
              );
            } else {
              this.loadNextPage(false);
            }
          }
        });
      },
      {
        root: scrollContainer, // Utiliser le conteneur scrollable comme root
        rootMargin: "100px", // Charger quand on est à 100px du bas
      },
    );

    this.intersectionObserver.observe(this.sentinelRef.value);
  }

  protected renderWidget() {
    if (this.loading) {
      return html`
        <div class="loading">
          <div class="loading-spinner"></div>
          <p>${t("loading", this.language)}</p>
        </div>
      `;
    }

    if (this.error) {
      return this.renderErrorScreen();
    }

    const hasFilterFields =
      this.config?.contentCustomization?.filterFields &&
      this.config.contentCustomization.filterFields.length > 0;
    const showFiltersConfig =
      hasFilterFields &&
      (this.config?.templateSpecificConfig as any)?.config?.showFilters !==
        false;
    const showFilters = showFiltersConfig && this.filtersVisible;
    const layoutFraction = this.getLayoutFraction();

    return html`
      <apidae-widget-layout
        .config=${{
          fraction: this.hideHeaderAndFilters ? "full" : layoutFraction,
          showFilters: this.hideHeaderAndFilters ? false : showFilters,
          headerSlot: this.hideHeaderAndFilters ? undefined : "header",
          filtersSlot: this.hideHeaderAndFilters
            ? undefined
            : showFilters
              ? "filters"
              : undefined,
          listSlot: "list",
        }}
      >
        ${!this.hideHeaderAndFilters
          ? html`
              <div slot="header">
                <apidae-widget-header
                  .config=${{
                    widgetName: this.config?.name,
                    logo: this.config?.visualCustomization?.logo,
                    logoUrl: this.config?.visualCustomization?.logoUrl,
                    language: this.language,
                    showFiltersButton: showFiltersConfig,
                    showClearButton: showFiltersConfig,
                    hasActiveFilters: this.hasActiveFilters(),
                    showSwitcher: false,
                    filtersVisible: this.filtersVisible,
                    resultsCount: this.totalItems,
                    primaryColor:
                      this.config?.visualCustomization?.colors?.primary,
                    secondaryColor:
                      this.config?.visualCustomization?.colors?.secondary,
                  }}
                  @toggle-filters=${this.handleToggleFilters}
                  @clear-filters=${this.handleClearFilters}
                ></apidae-widget-header>
              </div>

              ${showFilters
                ? html`
                    <div slot="filters" style="flex: 1;">
                      <apidae-filter-panel
                        .filterGroups=${this.filterGroups}
                        .language=${this.language}
                        .activeFilters=${this.activeFilters}
                        .dateRange=${this.dateRange}
                        @filter-change=${this.handleFilterChange}
                      ></apidae-filter-panel>
                    </div>
                  `
                : ""}
            `
          : ""}

        <div slot="list" class="list-main">
          ${this.items.length === 0 && !this.hideEmptyState
            ? html`
                <div class="empty-state">
                  <div class="empty-state-title">
                    ${t("noResultsFound", this.language) ||
                    "Aucun résultat trouvé."}
                  </div>
                  <div class="empty-state-subtitle">
                    ${t("adjustSearchWithFilters", this.language) ||
                    "Ajustez votre recherche grâce aux filtres."}
                  </div>
                </div>
              `
            : html`
                <div class="list-container">
                  <div class="list-grid">
                    ${this.items.map((item: ObjetTouristique) =>
                      this.renderListItem(item),
                    )}
                  </div>

                  ${this.hasMore
                    ? html`<div
                        ${ref(this.sentinelRef)}
                        style="height: 1px;"
                      ></div>`
                    : ""}
                </div>
              `}
        </div>
      </apidae-widget-layout>
    `;
  }

  private renderListItem(item: ObjetTouristique) {
    const imageUrl = getImageUrl(item, "diaporama");
    const nom = getLabel(item.nom, this.language);

    // Récupérer les 2 premiers champs de displayedFieldsPreview
    const displayedFields =
      this.config?.contentCustomization?.displayedFieldsPreview || [];
    const fieldsToShow = displayedFields.slice(0, 2);

    // Récupérer la typography pour le titre et le texte
    const typography = this.config?.visualCustomization?.typography;
    const titleStyle = typography
      ? {
          fontFamily: StyleService.getFontFamily(typography.titleFont),
          color: typography.titleColor,
          fontSize: `${typography.titleSize}px`,
        }
      : {
          fontFamily: "inherit",
          color: "#2F2F2F",
          fontSize: "16px",
        };
    const textStyle = typography
      ? {
          fontFamily: StyleService.getFontFamily(typography.textFont),
          color: typography.textColor,
          fontSize: `${typography.textSize}px`,
        }
      : {
          fontFamily: "inherit",
          color: "#2F2F2F",
          fontSize: "14px",
        };

    // Filtrer les champs non null pour gérer l'espace et le rendu
    // Utiliser getFieldData directement car getFieldValue peut retourner null même si le champ doit être affiché (ex: reservation avec url)
    const nonNullFields = fieldsToShow.filter(
      (field) => this.getFieldData(item, field.id) !== null,
    );
    const fieldsCount = nonNullFields.length;
    const hasOneField = fieldsCount === 1;
    const hasTwoFields = fieldsCount === 2;

    const isSelected = this.selectedItemId === item.id;

    return html`
      <div
        class="list-card ${isSelected ? "selected" : ""}"
        data-item-id="${item.id}"
        @click=${(e: Event) => {
          // Ne pas déclencher le clic si on clique sur le bouton de réservation
          const target = e.target as HTMLElement;
          if (target.closest(".list-card-reservation-button")) {
            e.stopPropagation();
            return;
          }
          if (this.disableDetailView) {
            // En mode composition (ListMapWidget), ne faire que sélectionner sans ouvrir le détail
            this.dispatchEvent(
              new CustomEvent("item-click", {
                detail: { item },
                bubbles: true,
                composed: true,
              }),
            );
          } else {
            this.handleItemClick(item);
          }
        }}
      >
        <div class="list-card-image-wrapper">
          <div class="card-image-fallback">${nom.charAt(0).toUpperCase()}</div>
          ${imageUrl && !this.failedImages.has(item.id)
            ? html`<img
                class="list-card-image ${this.loadedImages.has(item.id)
                  ? "loaded"
                  : ""}"
                src="${imageUrl}"
                alt="${nom}"
                loading="lazy"
                @load=${() => {
                  this.loadedImages.add(item.id);
                  this.requestUpdate();
                }}
                @error=${() => {
                  this.failedImages.add(item.id);
                  this.requestUpdate();
                }}
              />`
            : ""}
        </div>
        <div class="list-card-text">
          <h3
            class="list-card-title"
            data-fields-count="${fieldsCount}"
            style="font-family: ${titleStyle.fontFamily}; color: ${titleStyle.color}; font-size: ${titleStyle.fontSize};"
          >
            ${nom}
          </h3>
          <div
            class="list-card-fields"
            data-fields-count="${fieldsCount}"
            style="font-family: ${textStyle.fontFamily}; color: ${textStyle.color}; font-size: ${textStyle.fontSize};"
          >
            ${nonNullFields.map((field) =>
              this.renderField(
                item,
                field.id,
                textStyle,
                hasOneField,
                hasTwoFields,
              ),
            )}
          </div>
        </div>
      </div>
    `;
  }

  private renderField(
    item: ObjetTouristique,
    fieldId: string,
    textStyle: any,
    hasOneField: boolean,
    hasTwoFields: boolean,
  ) {
    const fieldData = this.getFieldData(item, fieldId);
    if (!fieldData) return null;

    // Pour reservation, c'est un bouton spécial
    if (fieldId === DisplayedFieldPreviewId.RESERVATION) {
      return html`
        <div
          class="list-card-field list-card-reservation-field"
          style="font-family: ${textStyle.fontFamily}; color: ${textStyle.color}; font-size: ${textStyle.fontSize};"
        >
          <div
            class="list-card-field-icon"
            style="width: 18px; height: 18px;"
          ></div>
          <button
            class="list-card-reservation-button"
            style="color: ${textStyle.color}; border-color: ${textStyle.color}; font-size: ${textStyle.fontSize};"
            @click=${(e: Event) => {
              e.stopPropagation();
              if (fieldData.url) {
                window.open(fieldData.url, "_blank");
              }
            }}
          >
            <apidae-icon
              name="bold/messages_conversation/square_forward"
              size="18"
              color="#2F2F2F"
            ></apidae-icon>
            <span>${t("reservation", this.language) || "Réservation"}</span>
          </button>
        </div>
      `;
    }

    const iconName = getFieldIconName(fieldId);

    return html`
      <div
        class="list-card-field"
        data-field-id="${fieldId}"
        data-has-one-field="${hasOneField}"
        data-has-two-fields="${hasTwoFields}"
        style="font-family: ${textStyle.fontFamily}; color: ${textStyle.color}; font-size: ${textStyle.fontSize};"
      >
        <apidae-icon
          name="${iconName}"
          size="18"
          color="#2F2F2F"
          class="list-card-field-icon"
        ></apidae-icon>
        <span class="list-card-field-text"> ${fieldData.value} </span>
      </div>
    `;
  }

  private getFieldData(
    item: ObjetTouristique,
    fieldId: string,
  ): { value: string; url?: string } | null {
    return getFieldData(item, fieldId, {
      language: this.language,
      getLabel: (libelle) => getLabel(libelle, this.language),
      formatDate: (dateStr, options) =>
        formatDate(dateStr, this.language, options),
      t: (key) => t(key, this.language),
    });
  }

  render() {
    return html`
      <div class="widget-container">
        <div class="widget-content">${this.renderWidget()}</div>
        ${this.renderDetailView()} ${this.renderToast()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "apidae-widget-liste": ListWidget;
  }
}
